import { Divider, Stack } from "@mui/material";
import { Row, SmText, ALink, XsText } from "Components";
import { formatDatetime } from "helpers/datetime";
import { useState } from "react";
import { apiMarkInquirySeen } from "services/api/inquiry";
import { useNotify } from "providers/notify";
import { DIRECT_PATHS } from "constants/routes";
import { BizInquiryType } from "types/inquiry";
import useMediaBreakpoints from "hooks/useMediaBreakpoints";
import { useLoading } from "providers/loading";
import { formatError } from "helpers/error";
import { useDispatch } from "react-redux";
import { onReloadMenuCountData } from "redux/actions/common-data";
import { useHistory } from "react-router-dom";
import { COLORS } from "constants/layout";
import { StyledInquiry } from "./Inquiry.styles";
import { GreenButton, ChatNowButton } from "../button/index";
import { useTranslation } from "hooks/useTranslation";
import { SparkleIcon } from "Assets/icons/sparkle";
import { DubbleCheckIcon, SmLockIcon } from "Assets/icons";
import { Link } from "Components/link";

interface InquiryProps {
  inquiry: BizInquiryType;
  customDetailLink?: string;
}

const Inquiry = ({ inquiry, customDetailLink }: InquiryProps) => {
  const t = useTranslation();
  const { setLoading } = useLoading();
  const { setNotify } = useNotify();
  const [isSeen, setISeen] = useState(inquiry.is_seen === 1);
  const { downMobileBreakpoint: isMobile } = useMediaBreakpoints();
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    identity,
    farm_item_name,
    farm_item = "",
    business_types,
    year_established,
    num_employees,
    annual_revenue_amount,
    import_countries,
    content,
    summary = "abc",
    customer_name,
    is_owned_contact,
    customer,
  } = inquiry;
  // const [openUnlockPopup, setOpenUnlockPopup] = useState(false);

  const buyerInfos = [
    { label: "business_type", value: business_types },
    { label: "year_established", value: year_established },
    { label: "number_of_employees", value: num_employees },
    { label: "annual_sales_revenue", value: annual_revenue_amount },
    { label: "import_history", value: import_countries },
  ].filter((info) => info.value);

  const onDirectToInquiryDetail = (id: string) => {
    if (customDetailLink) {
      history.push(customDetailLink);
    } else {
      history.push(DIRECT_PATHS.inquiryDetail(id));
    }
  };

  const onClickInquiry = () => {
    const inquiryId = inquiry.name;

    // handle mark seen inquiry
    if (!isSeen) {
      setLoading(true);
      apiMarkInquirySeen(inquiryId)
        .then(() => {
          setISeen(true);
          dispatch(onReloadMenuCountData());
        })
        .catch((err) =>
          setNotify({
            open: true,
            msg: formatError(err)?.message,
            type: "error",
          })
        )
        .finally(() => setLoading(false));
    }

    onDirectToInquiryDetail(inquiryId);
  };

  // const onCloseUnlock = () => setOpenUnlockPopup(false);

  return (
    <>
      <StyledInquiry
        className={isSeen ? "is-seen" : ""}
        onClick={onClickInquiry}
        spacing={1}
      >
        <Stack spacing={0.5}>
          <Stack
            justifyContent="space-between"
            direction="row"
            alignItems="center"
          >
            <SmText
              color={COLORS.gray[500]}
              className="ellipsis-text buyer-infos"
              maxWidth="calc(100% - 80px)"
            >
              {t("from")}:{" "}
              <ALink
                href={
                  is_owned_contact
                    ? DIRECT_PATHS.buyerDetail(customer, true)
                    : undefined
                }
                onClick={(e) => e.stopPropagation()}
              >
                {customer_name}
              </ALink>
            </SmText>

            <Stack direction="row" spacing={1} alignItems="center">
              {isSeen && (
                <XsText fontSize={10}>
                  <DubbleCheckIcon
                    width={12}
                    height={7}
                    style={{ marginRight: 4 }}
                  />
                  {t("seen")}
                </XsText>
              )}
              {!is_owned_contact && (
                <span className="lock-icon">
                  <SmLockIcon
                    width={12}
                    height={12}
                    stroke={COLORS.yellow[500]}
                  />
                </span>
              )}
            </Stack>
          </Stack>
          <Divider />
        </Stack>

        <Stack spacing={1}>
          {buyerInfos.length > 0 && (
            <Stack direction="row" gap={0.5} flexWrap="wrap">
              {buyerInfos.map((info, i) => (
                <XsText className="buyer-infos" key={i}>
                  {t(info.label)}: <span>{info.value}</span>
                </XsText>
              ))}
            </Stack>
          )}

          {/* Product name */}
          {farm_item_name && (
            <XsText className="ellipsis-text buyer-infos">
              {t("about_product")}:{" "}
              <ALink href={DIRECT_PATHS.homeProductDetail(farm_item)}>
                {farm_item_name}
              </ALink>
            </XsText>
          )}

          {/* Summary */}
          {!is_owned_contact && summary && (
            <XsText color={COLORS.green[600]}>
              {t("freshdi_ai")}
              <SparkleIcon style={{ margin: "0 4px" }} />
              <span style={{ color: COLORS.gray[900] }}>
                {t("summarized_from")}: {summary}
              </span>
            </XsText>
          )}

          {is_owned_contact && content && (
            <XsText className="cutoff-text twoline buyer-infos">
              {t("message")}: <span>{content}</span>
            </XsText>
          )}

          {inquiry?.rfq && (
            <XsText
              className={
                "inquiry " +
                (isMobile ? "cutoff-text twoline" : "ellipsis-text")
              }
            >
              {t("rfq_attached")}:{" "}
              <Link to={DIRECT_PATHS.rfqDetail(inquiry.rfq, true)}>
                <span>{inquiry.rfq_title}</span>
              </Link>
            </XsText>
          )}
        </Stack>

        <Stack
          spacing={1}
          direction={"row"}
          alignItems="flex-end"
          justifyContent="space-between"
        >
          <XsText className="creation" color={COLORS.green[600]}>
            {t("inquired_on_date", {
              date: formatDatetime(inquiry.creation, "DD MMM YYYY"),
            })}
          </XsText>

          <Row spacing={1} className="btns">
            <GreenButton
              onClick={(e) => {
                e.stopPropagation();
                onClickInquiry();
              }}
              inheritHeight={true}
            >
              {t("view_detail")}
            </GreenButton>
            <ChatNowButton identity={identity} isFarmChat={true} />
          </Row>
        </Stack>
      </StyledInquiry>

      {/* <Dialog open={openUnlockPopup} onClose={onCloseUnlock}>
        <InquiryUnlockPopup onClose={onCloseUnlock} inquiry_id={name} />
      </Dialog> */}
    </>
  );
};

export default Inquiry;
